import React from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { ChevronRight, LayoutList } from "lucide-react";

/**
 * EmailCreativeCatalogTable — DASH-EMAIL-011a, cut over in DASH-EMAIL-012A
 * to the real Creative rollup (GET creative-analytics/creatives).
 *
 * `firstUsed`/`lastUsed` are gone (DASH-EMAIL-012A Frozen Decision 11): no
 * real execution/use timestamp exists anywhere in the governed catalog —
 * removed rather than derived from any created/updated timestamp. The
 * three separate illustrative metric columns collapse into one real
 * `channel2_metrics` column, same as EmailAngleTable/EmailAssetTable — the
 * real rollup returns a single Channel-2 availability object per row, not
 * a per-outcome breakdown.
 *
 * The "×N versions" badge stays always-visible next to the Creative name
 * (unchanged from 011a).
 */
function MetricCell({ metric, testId }) {
  const isUnavailable = metric?.availability === "UNAVAILABLE";
  const display = isUnavailable ? "—" : Number(metric?.value || 0).toLocaleString();
  return (
    <TableCell className="text-right font-mono text-slate-100" data-testid={testId}>
      <span data-metric-availability={isUnavailable ? "unavailable" : "available"}>{display}</span>
    </TableCell>
  );
}

export default function EmailCreativeCatalogTable({ rows, angleLookup, campaignLookup, onSelectCreative }) {
  const data = rows || [];

  if (data.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="email-creative-catalog-table-empty">
        <CardContent className="p-8 text-center text-slate-400">
          <p className="text-lg">No Creatives for this selection</p>
          <p className="text-sm mt-2">Try a different Program, Campaign, or Angle filter.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-creative-catalog-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <LayoutList className="h-5 w-5 text-indigo-400" />
          Creatives
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-8 text-slate-400" />
              <TableHead className="text-slate-400">Creative</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-right text-slate-400">Versions</TableHead>
              <TableHead className="text-right text-slate-400">Broadcasts</TableHead>
              <TableHead className="text-right text-slate-400">Channel-2 Outcomes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => {
              const angleName = row.angle_id ? angleLookup?.[row.angle_id] || row.angle_id : "—";
              const campaignName = campaignLookup?.[row.campaign_id] || row.campaign_id;
              return (
                <TableRow
                  key={row.creative_id}
                  className="cursor-pointer border-white/10 hover:bg-white/5"
                  data-testid={`email-creative-catalog-row-${row.creative_id}`}
                  onClick={() => onSelectCreative?.(row.creative_id)}
                >
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-slate-400" data-testid={`email-creative-catalog-open-${row.creative_id}`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-100">{row.name}</span>
                      <Badge
                        className="bg-indigo-500/20 text-indigo-300 text-xs"
                        data-testid={`email-creative-catalog-version-badge-${row.creative_id}`}
                      >
                        {row.version_count} {row.version_count === 1 ? "version" : "versions"}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-400">
                      {row.creative_id} · {angleName} · {campaignName}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-300">{row.status}</TableCell>
                  <TableCell className="text-right font-mono text-slate-100">{row.version_count.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-slate-100">{row.broadcast_count.toLocaleString()}</TableCell>
                  <MetricCell metric={row.channel2_metrics} testId={`email-creative-catalog-channel2-${row.creative_id}`} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
